import React, { useState, useRef } from 'react';
import { UserCheck, Car, Ticket, FileText, Briefcase, GraduationCap, Home, Zap, Heart, Globe, ChevronLeft, ChevronRight } from 'lucide-react';

const iconMap = {
  'UserCheck': UserCheck,
  'Car': Car,
  'Ticket': Ticket,
  'FileText': FileText,
  'Briefcase': Briefcase,
  'GraduationCap': GraduationCap,
  'Home': Home,
  'Zap': Zap,
  'Heart': Heart,
  'Globe': Globe
};

const Services = ({ language, content }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const tabsRef = useRef(null);
  const t = content[language];

  const checkScroll = () => {
    if (tabsRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tabsRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scroll = (direction) => {
    if (tabsRef.current) {
      const scrollAmount = 300;
      tabsRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
      setTimeout(checkScroll, 300);
    }
  };

  React.useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, []);

  return (
    <>
      <style>{`
        .services {
          padding: 80px 0;
          background: linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%);
          position: relative;
        }

        .section-header {
          text-align: center;
          margin-bottom: 40px;
        }

        .section-header h2 {
          font-size: 40px;
          color: #1a1a1a;
          margin-bottom: 16px;
          font-weight: 700;
          background: linear-gradient(135deg, #1a1a1a 0%, #ff6b35 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .section-header p {
          font-size: 18px;
          color: #666;
        }

        .tabs-container {
          margin-bottom: 20px;
          position: relative;
        }

        .tabs-wrapper {
          position: relative;
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .scroll-button {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: white;
          border: 2px solid #e0e0e0;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s;
          flex-shrink: 0;
          z-index: 10;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .scroll-button:hover {
          background: #ff6b35;
          border-color: #ff6b35;
          color: white;
          transform: scale(1.1);
        }

        .scroll-button:disabled {
          opacity: 0;
          pointer-events: none;
        }

        .tabs {
          display: flex;
          gap: 8px;
          overflow-x: auto;
          padding: 16px 0;
          margin-bottom: 20px;
          border-bottom: 2px solid #f0f0f0;
          scroll-behavior: smooth;
          flex: 1;
        }

        .tabs::-webkit-scrollbar {
          height: 6px;
        }

        .tabs::-webkit-scrollbar-thumb {
          background: #ddd;
          border-radius: 3px;
        }

        .tab-button {
          font-family: 'Poppins', sans-serif;
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 14px 28px;
          background: white;
          border: 2px solid #e0e0e0;
          border-radius: 50px;
          cursor: pointer;
          transition: all 0.3s;
          white-space: nowrap;
          font-size: 15px;
          font-weight: 500;
          color: #666;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
        }

        .tab-button:hover {
          border-color: #ff6b35;
          color: #ff6b35;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(255, 107, 53, 0.15);
        }

        .tab-button.active {
          background: linear-gradient(135deg, #ff6b35 0%, #ff8c42 100%);
          border-color: #ff6b35;
          color: white;
          box-shadow: 0 6px 20px rgba(255, 107, 53, 0.3);
        }

        .tab-content {
          animation: fadeIn 0.4s;
        }

        .services-list {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 16px;
        }

        .service-item {
          background: white;
          padding: 20px 24px;
          border-radius: 12px;
          border-left: 4px solid #ff6b35;
          transition: all 0.3s;
          display: flex;
          align-items: center;
          gap: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
          position: relative;
          overflow: hidden;
        }

        .service-item::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 4px;
          height: 100%;
          background: linear-gradient(180deg, #ff6b35 0%, #ff8c42 100%);
        }

        .service-item:hover {
          background: linear-gradient(135deg, #fff5f2 0%, #ffe5dc 100%);
          transform: translateX(8px);
          box-shadow: 0 8px 24px rgba(255, 107, 53, 0.15);
          border-left-color: #ff8c42;
        }

        .service-bullet {
          width: 10px;
          height: 10px;
          background: linear-gradient(135deg, #ff6b35 0%, #ff8c42 100%);
          border-radius: 50%;
          flex-shrink: 0;
          box-shadow: 0 2px 6px rgba(255, 107, 53, 0.3);
        }

        .service-item span {
          color: #333;
          font-size: 15px;
          font-weight: 500;
        }

        @media (max-width: 768px) {
          .services {
            padding: 60px 0;
          }

          .services-list {
            grid-template-columns: 1fr;
          }

          .tabs-wrapper {
            gap: 8px;
          }

          .scroll-button {
            width: 36px;
            height: 36px;
          }

          .tabs {
            gap: 6px;
          }

          .tab-button {
            padding: 10px 16px;
            font-size: 14px;
          }

          .section-header h2 {
            font-size: 28px;
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>

      <section id="services" className="services">
        <div className="container">
          <div className="section-header">
            <h2>{t.services.title}</h2>
            <p>{t.services.subtitle}</p>
          </div>

          <div className="tabs-container">
            <div className="tabs-wrapper">
              <button
                className="scroll-button"
                onClick={() => scroll('left')}
                disabled={!showLeftArrow}
                aria-label="Scroll left"
              >
                <ChevronLeft size={20} />
              </button>

              <div className="tabs" ref={tabsRef} onScroll={checkScroll}>
                {t.services.categories.map((category, index) => {
                  const Icon = iconMap[category.icon] || Globe;
                  return (
                    <button
                      key={index}
                      className={`tab-button ${activeTab === index ? 'active' : ''}`}
                      onClick={() => setActiveTab(index)}
                    >
                      <Icon size={18} />
                      {category.name}
                    </button>
                  );
                })}
              </div>

              <button
                className="scroll-button"
                onClick={() => scroll('right')}
                disabled={!showRightArrow}
                aria-label="Scroll right"
              >
                <ChevronRight size={20} />
              </button>
            </div>

            <div className="tab-content">
              <div className="services-list">
                {t.services.categories[activeTab].services.map((service, index) => (
                  <div key={index} className="service-item">
                    <div className="service-bullet"></div>
                    <span>{service}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Services;

// import React, { useState } from 'react';
// import { 
//   UserCheck, Car, Ticket, FileText, Briefcase, GraduationCap, 
//   Home, Zap, Heart, Globe, CheckCircle, ArrowRight, Star 
// } from 'lucide-react';

// const iconMap = {
//   'UserCheck': UserCheck,
//   'Car': Car,
//   'Ticket': Ticket,
//   'FileText': FileText,
//   'Briefcase': Briefcase,
//   'GraduationCap': GraduationCap,
//   'Home': Home,
//   'Zap': Zap,
//   'Heart': Heart,
//   'Globe': Globe
// };

// const Services = ({ language, content }) => {
//   const [activeTab, setActiveTab] = useState(0);
//   const t = content[language];

//   return (
//     <>
//       <style>{`
//         .services {
//           padding: 100px 0;
//           background: linear-gradient(180deg, #ffffff 0%, #f8f9fa 50%, #fff5f2 100%);
//           position: relative;
//           overflow: hidden;
//         }

//         .services::before {
//           content: '';
//           position: absolute;
//           top: 0;
//           right: -200px;
//           width: 600px;
//           height: 600px;
//           background: radial-gradient(circle, rgba(255, 107, 53, 0.08) 0%, transparent 70%);
//           border-radius: 50%;
//         }

//         .services::after {
//           content: '';
//           position: absolute;
//           bottom: -150px;
//           left: -150px;
//           width: 500px;
//           height: 500px;
//           background: radial-gradient(circle, rgba(255, 140, 66, 0.06) 0%, transparent 70%);
//           border-radius: 50%;
//         }

//         .section-header {
//           text-align: center;
//           margin-bottom: 60px;
//           position: relative;
//           z-index: 1;
//         }

//         .section-header h3 {
//           font-size: 46px;
//           color: #1a1a1a;
//           margin-bottom: 16px;
//           font-weight: 700;
//           background: linear-gradient(135deg, #1a1a1a 0%, #333 100%);
//           -webkit-background-clip: text;
//           -webkit-text-fill-color: transparent;
//           background-clip: text;
//         }

//         .section-header p {
//           font-size: 19px;
//           color: #666;
//           max-width: 600px;
//           margin: 0 auto;
//         }

//         .tabs-container {
//           position: relative;
//           z-index: 1;
//         }

//         .tabs {
//           display: flex;
//           gap: 12px;
//           overflow-x: auto;
//           padding: 20px 0;
//           margin-bottom: 40px;
//           justify-content: center;
//           flex-wrap: wrap;
//         }

//         .tabs::-webkit-scrollbar {
//           height: 8px;
//         }

//         .tabs::-webkit-scrollbar-thumb {
//           background: #ddd;
//           border-radius: 4px;
//         }

//         .tab-button {
//           font-family: 'Poppins', sans-serif;
//           display: flex;
//           align-items: center;
//           gap: 10px;
//           padding: 16px 32px;
//           background: white;
//           border: 2px solid #e0e0e0;
//           border-radius: 50px;
//           cursor: pointer;
//           transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
//           white-space: nowrap;
//           font-size: 16px;
//           font-weight: 600;
//           color: #666;
//           box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
//           position: relative;
//           overflow: hidden;
//         }

//         .tab-button::before {
//           content: '';
//           position: absolute;
//           top: 0;
//           left: -100%;
//           width: 100%;
//           height: 100%;
//           background: linear-gradient(135deg, #ff6b35 0%, #ff8c42 100%);
//           transition: left 0.4s ease;
//           z-index: 0;
//         }

//         .tab-button > * {
//           position: relative;
//           z-index: 1;
//         }

//         .tab-button:hover {
//           border-color: #ff6b35;
//           transform: translateY(-3px);
//           box-shadow: 0 8px 20px rgba(255, 107, 53, 0.2);
//         }

//         .tab-button:hover::before {
//           left: 0;
//         }

//         .tab-button:hover {
//           color: white;
//         }

//         .tab-button.active {
//           background: linear-gradient(135deg, #ff6b35 0%, #ff8c42 100%);
//           border-color: #ff6b35;
//           color: white;
//           box-shadow: 0 8px 24px rgba(255, 107, 53, 0.35);
//           transform: translateY(-2px);
//         }

//         .tab-button.active::before {
//           left: 0;
//         }

//         .service-count {
//           background: rgba(255, 255, 255, 0.25);
//           padding: 2px 10px;
//           border-radius: 20px;
//           font-size: 13px;
//           font-weight: 700;
//         }

//         .tab-button.active .service-count {
//           background: rgba(255, 255, 255, 0.3);
//         }

//         .tab-content {
//           animation: fadeInUp 0.5s ease;
//         }

//         @keyframes fadeInUp {
//           from {
//             opacity: 0;
//             transform: translateY(20px);
//           }
//           to {
//             opacity: 1;
//             transform: translateY(0);
//           }
//         }

//         .services-grid {
//           display: grid;
//           grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
//           gap: 24px;
//           margin-bottom: 40px;
//         }

//         .service-card {
//           background: white;
//           padding: 28px;
//           border-radius: 16px;
//           border: 2px solid #f0f0f0;
//           transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
//           position: relative;
//           overflow: hidden;
//           cursor: pointer;
//           display: flex;
//           flex-direction: column;
//           gap: 12px;
//         }

//         .service-card::before {
//           content: '';
//           position: absolute;
//           top: 0;
//           left: 0;
//           width: 100%;
//           height: 4px;
//           background: linear-gradient(90deg, #ff6b35 0%, #ff8c42 100%);
//           transform: scaleX(0);
//           transform-origin: left;
//           transition: transform 0.4s ease;
//         }

//         .service-card:hover::before {
//           transform: scaleX(1);
//         }

//         .service-card:hover {
//           border-color: #ff6b35;
//           transform: translateY(-8px);
//           box-shadow: 0 16px 40px rgba(255, 107, 53, 0.15);
//           background: linear-gradient(135deg, #fff 0%, #fff5f2 100%);
//         }

//         .service-card-header {
//           display: flex;
//           align-items: center;
//           gap: 12px;
//         }

//         .service-icon {
//           width: 50px;
//           height: 50px;
//           background: linear-gradient(135deg, #fff5f2 0%, #ffe5dc 100%);
//           border-radius: 12px;
//           display: flex;
//           align-items: center;
//           justify-content: center;
//           color: #ff6b35;
//           transition: all 0.4s ease;
//         }

//         .service-card:hover .service-icon {
//           background: linear-gradient(135deg, #ff6b35 0%, #ff8c42 100%);
//           color: white;
//           transform: scale(1.1) rotate(5deg);
//         }

//         .service-title {
//           font-size: 17px;
//           font-weight: 600;
//           color: #1a1a1a;
//           flex: 1;
//         }

//         .popular-badge {
//           position: absolute;
//           top: 12px;
//           right: 12px;
//           background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
//           color: #1a1a1a;
//           padding: 4px 12px;
//           border-radius: 20px;
//           font-size: 11px;
//           font-weight: 700;
//           display: flex;
//           align-items: center;
//           gap: 4px;
//           box-shadow: 0 2px 8px rgba(255, 215, 0, 0.3);
//         }

//         .service-description {
//           color: #666;
//           font-size: 14px;
//           line-height: 1.6;
//           display: none;
//         }

//         .service-card:hover .service-description {
//           display: block;
//           animation: fadeIn 0.3s ease;
//         }

//         @keyframes fadeIn {
//           from { opacity: 0; }
//           to { opacity: 1; }
//         }

//         .service-action {
//           display: flex;
//           align-items: center;
//           gap: 8px;
//           color: #ff6b35;
//           font-size: 14px;
//           font-weight: 600;
//           margin-top: auto;
//           opacity: 0;
//           transform: translateX(-10px);
//           transition: all 0.3s ease;
//         }

//         .service-card:hover .service-action {
//           opacity: 1;
//           transform: translateX(0);
//         }

//         .stats-section {
//           display: grid;
//           grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
//           gap: 24px;
//           margin-top: 60px;
//           padding: 40px;
//           background: linear-gradient(135deg, #fff 0%, #fff5f2 100%);
//           border-radius: 20px;
//           border: 2px solid #ffe5dc;
//         }

//         .stat-item {
//           text-align: center;
//         }

//         .stat-number {
//           font-size: 36px;
//           font-weight: 700;
//           background: linear-gradient(135deg, #ff6b35 0%, #ff8c42 100%);
//           -webkit-background-clip: text;
//           -webkit-text-fill-color: transparent;
//           background-clip: text;
//           margin-bottom: 8px;
//         }

//         .stat-label {
//           color: #666;
//           font-size: 15px;
//         }

//         @media (max-width: 768px) {
//           .services {
//             padding: 60px 0;
//           }

//           .section-header h3 {
//             font-size: 32px;
//           }

//           .section-header p {
//             font-size: 16px;
//           }

//           .services-grid {
//             grid-template-columns: 1fr;
//             gap: 16px;
//           }

//           .tabs {
//             justify-content: flex-start;
//             flex-wrap: nowrap;
//           }

//           .tab-button {
//             padding: 12px 20px;
//             font-size: 14px;
//           }

//           .stats-section {
//             grid-template-columns: repeat(2, 1fr);
//             padding: 24px;
//           }

//           .stat-number {
//             font-size: 28px;
//           }
//         }
//       `}</style>

//       <section id="services" className="services">
//         <div className="container">
//           <div className="section-header">
//             <h3>{t.services.title}</h3>
//             <p>{t.services.subtitle}</p>
//           </div>

//           <div className="tabs-container">
//             <div className="tabs">
//               {t.services.categories.map((category, index) => {
//                 const Icon = iconMap[category.icon] || Globe;
//                 return (
//                   <button
//                     key={index}
//                     className={`tab-button ${activeTab === index ? 'active' : ''}`}
//                     onClick={() => setActiveTab(index)}
//                   >
//                     <Icon size={20} />
//                     <span>{category.name}</span>
//                     <span className="service-count">{category.services.length}</span>
//                   </button>
//                 );
//               })}
//             </div>

//             <div className="tab-content">
//               <div className="services-grid">
//                 {t.services.categories[activeTab].services.map((service, index) => (
//                   <div key={index} className="service-card">
//                     {index < 3 && (
//                       <div className="popular-badge">
//                         <Star size={12} fill="currentColor" />
//                         {language === 'mr' ? 'लोकप्रिय' : 'Popular'}
//                       </div>
//                     )}
//                     <div className="service-card-header">
//                       <div className="service-icon">
//                         <CheckCircle size={24} />
//                       </div>
//                       <div className="service-title">{service}</div>
//                     </div>
//                     <div className="service-description">
//                       {language === 'mr' 
//                         ? 'त्वरित आणि सोपी प्रक्रिया. आमच्या तज्ञ तुम्हाला मदत करतील.'
//                         : 'Quick and easy process. Our experts will assist you.'}
//                     </div>
//                     <div className="service-action">
//                       {language === 'mr' ? 'अधिक जाणून घ्या' : 'Learn More'}
//                       <ArrowRight size={16} />
//                     </div>
//                   </div>
//                 ))}
//               </div>
//             </div>
//           </div>

//           {/* Stats Section */}
//           <div className="stats-section">
//             <div className="stat-item">
//               <div className="stat-number">500+</div>
//               <div className="stat-label">
//                 {language === 'mr' ? 'समाधानी ग्राहक' : 'Happy Customers'}
//               </div>
//             </div>
//             <div className="stat-item">
//               <div className="stat-number">50+</div>
//               <div className="stat-label">
//                 {language === 'mr' ? 'सेवा उपलब्ध' : 'Services Available'}
//               </div>
//             </div>
//             <div className="stat-item">
//               <div className="stat-number">100%</div>
//               <div className="stat-label">
//                 {language === 'mr' ? 'विश्वसनीयता' : 'Reliability'}
//               </div>
//             </div>
//             <div className="stat-item">
//               <div className="stat-number">24/7</div>
//               <div className="stat-label">
//                 {language === 'mr' ? 'समर्थन' : 'Support'}
//               </div>
//             </div>
//           </div>
//         </div>
//       </section>
//     </>
//   );
// };

// export default Services;